// deps
import React, { ReactElement, memo, useCallback, useMemo, useState } from "react";

// internal deps
import { CardDetails, ProceedNewCardDetails } from "@swiggy-private/payment-types";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// helpers
import { getFormattedCardName } from "@payment-ui/helpers/Card";
import { format, getCloudinaryUrl, InputFieldAttributes } from "@swiggy-private/core-ui";
import { capitalizeFirstLetter } from "@payment-ui/helpers/String";
import { isNonEmptyString } from "@swiggy-private/payment-core";

// constants
import { TEXTS } from "@payment-ui/constants/index";
import { PENNY_TXN_AMOUNT } from "@payment-ui/constants/Card";

// components
import Input from "@payment-ui/components/add-new-card/components/Input";
import { PaymentMethodIcon } from "@payment-ui/widgets/payment-method-icon";

// testids
import { ADD_NEW_CARD_TEST_IDS, VERIFY_CVV_TEST_IDS } from "@swiggy-private/payment-test-ids";

// styles
import styles from "./VerifyCvv.scss";

export interface VerifyCvvProps {
    renderingDetails: Pick<CardDetails, "cardBrand" | "iconUrl" | "cardNumber">;
    readonly cardDetails: CardDetails | ProceedNewCardDetails;
    onPay: (cardDetails: CardDetails | ProceedNewCardDetails, extraParams: { cvv: string }) => void;
    defaultCvv?: string;
    isNonSecureCard: boolean;
}

const CVV_ATTR = {
    attrs: {
        autoComplete: "off",
        pattern: "[0-9]*",
        inputMode: "numeric",
        "data-testid": ADD_NEW_CARD_TEST_IDS.CVV,
        "data-disable-copy-paste": "true",
    } as InputFieldAttributes,
    errorLabel: TEXTS.INVALID_CVV,
    label: TEXTS.CVV,
    maxLength: 4,
    name: "cvv",
    type: "password",
};

function VerifyCvv(props: VerifyCvvProps): ReactElement {
    const { renderingDetails, cardDetails, onPay, defaultCvv, isNonSecureCard } = props;
    const [cvv, setCvv] = useState(props.defaultCvv || "");

    const onCvvChange = useCallback((value: string, _name?: string) => {
        setCvv(value);
    }, []);

    const isValidCvv = useMemo(() => {
        return cvv.length >= 3 && cvv.length <= 4;
    }, [cvv]);

    const titleCta = useMemo(() => {
        if (isNonSecureCard) {
            return TEXTS.SECURE_SAVE_YOUR_CARD;
        }
        return format(TEXTS.SECURE_YOUR_CARD_WITH_VISA_WITH_BRAND, capitalizeFirstLetter(renderingDetails.cardBrand));
    }, [isNonSecureCard, renderingDetails.cardBrand]);

    const subtextCta = useMemo(() => {
        if (isNonSecureCard) {
            return TEXTS.VERIFY_CVV_MESSAGE_SAVE_CARD;
        }
        return format(TEXTS.VERIFY_CVV_MESSAGE, capitalizeFirstLetter(renderingDetails.cardBrand));
    }, [isNonSecureCard, renderingDetails.cardBrand]);

    const buttonCta = useMemo(() => {
        if (isNonSecureCard) {
            return format(TEXTS.PAY_X_AND_ADD, PENNY_TXN_AMOUNT);
        }
        return format(TEXTS.PAY_X_AND_SECURE, PENNY_TXN_AMOUNT);
    }, [isNonSecureCard]);

    const handlePrimaryActionClick = useCallback((): void => {
        onPay(cardDetails, { cvv });
    }, [onPay, cvv, cardDetails]);

    const keepLabelAction = useMemo(() => {
        return isNonEmptyString(defaultCvv);
    }, [defaultCvv]);

    return (
        <div>
            <div className={styles.containerHeading}>
                <PaymentMethodIcon src={getCloudinaryUrl(renderingDetails.iconUrl)} width={36} />
                <div className={styles.containerHeadingText}>{getFormattedCardName(cardDetails.cardNumber)}</div>
            </div>
            <div className={styles.containerTitle} data-testid={VERIFY_CVV_TEST_IDS.cvvTitle}>
                {titleCta}
            </div>
            <div className={styles.containerMessage} data-testid={VERIFY_CVV_TEST_IDS.cvvSubTitle}>
                {subtextCta}
            </div>
            <Input
                className={styles.containerInput}
                value={cvv}
                onChange={onCvvChange}
                showError={false}
                {...CVV_ATTR}
                keepLabelActive={keepLabelAction}
                data-testid={VERIFY_CVV_TEST_IDS.cvvInput}
            />
            <div className={styles.containerButton}>
                <SuperButton
                    onClick={handlePrimaryActionClick}
                    isDisabled={!isValidCvv}
                    buttonType="specialPositive"
                    variant="fullwidth"
                    buttonSize="large"
                    className={styles.containerButtonText}
                    data-testid={VERIFY_CVV_TEST_IDS.primaryAction}
                >
                    {buttonCta}
                </SuperButton>
            </div>
        </div>
    );
}
export default memo(VerifyCvv);
